import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deactivateEmployee,
  listCustomFieldSchemas,
  listEmployeesFull,
  restoreEmployee,
  type EmployeeListQuery,
} from "@/api/lookups";
import type {
  CustomFieldSchemaResponse,
  EmployeeResponse,
  Folder,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/table/DataTable";
import { useCan } from "@/hooks/useCan";
import { useFolders } from "@/hooks/useFolders";
import { EmployeeFormDialog } from "@/components/registries/EmployeeFormDialog";

interface Filters {
  search: string;
  custom_field_key: string;
  custom_field_value: string;
}

const EMPTY: Filters = {
  search: "",
  custom_field_key: "",
  custom_field_value: "",
};

function toQuery(f: Filters, includeInactive: boolean): EmployeeListQuery {
  const q: EmployeeListQuery = { limit: 100 };
  if (f.search) q.search = f.search;
  if (f.custom_field_key) q.custom_field_key = f.custom_field_key;
  if (f.custom_field_value) q.custom_field_value = f.custom_field_value;
  if (includeInactive) q.include_inactive = true;
  return q;
}

export function EmployeesRegistry() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canCreate = useCan("employee_crud");
  const canDeactivate = useCan("employee_deactivate");
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [draft, setDraft] = useState<Filters>(EMPTY);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [creating, setCreating] = useState(false);

  // TM-035ae774: a row click now opens the employee card (where editing
  // lives) instead of the inline edit dialog. The `employeeId` param name
  // matches the route in `src/routes/employees.$employeeId.tsx`.
  const goToEmployee = useCallback(
    (row: EmployeeResponse) => {
      void navigate({
        to: "/employees/$employeeId",
        params: { employeeId: row.id },
      });
    },
    [navigate],
  );
  // Papki-tenants v1: folder scope drives the employees query key + query
  // string. See the equivalent comment in `ClientsRegistry`.
  const { activeFolderId, folders } = useFolders();
  const queryClient = useQueryClient();

  const employees = useQuery({
    queryKey: ["employees", "list", filters, activeFolderId, includeInactive],
    queryFn: () =>
      listEmployeesFull(toQuery(filters, includeInactive), activeFolderId),
  });
  const schemas = useQuery({
    queryKey: ["custom-fields", "employee"],
    queryFn: () => listCustomFieldSchemas("employee"),
  });

  const deactivateMut = useMutation({
    mutationFn: (id: string) => deactivateEmployee(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
  });
  const restoreMut = useMutation({
    mutationFn: (id: string) => restoreEmployee(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
  });

  const columns = useColumns(
    schemas.data ?? [],
    folders,
    canDeactivate,
    (employee) => {
      const msg = employee.is_active
        ? t("employees.confirm.deactivate", { name: employee.full_name })
        : t("employees.confirm.restore", { name: employee.full_name });
      if (typeof window !== "undefined" && !window.confirm(msg)) return;
      if (employee.is_active) deactivateMut.mutate(employee.id);
      else restoreMut.mutate(employee.id);
    },
    deactivateMut.isPending || restoreMut.isPending,
  );

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">
            {t("employees.registryTitle")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("employees.registrySubtitle")}
          </p>
        </div>
        {canCreate ? (
          <Button
            type="button"
            onClick={() => setCreating(true)}
            data-testid="open-create-employee"
          >
            {t("employees.createButton")}
          </Button>
        ) : null}
      </header>

      <form
        className="grid grid-cols-1 gap-2 md:grid-cols-4"
        onSubmit={(e) => {
          e.preventDefault();
          setFilters(draft);
        }}
        data-testid="employees-filters"
      >
        <Input
          aria-label={t("common.search")}
          placeholder={t("common.search")}
          value={draft.search}
          onChange={(e) => setDraft((d) => ({ ...d, search: e.target.value }))}
        />
        <select
          aria-label={t("adminCustomFields.fields.key")}
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={draft.custom_field_key}
          onChange={(e) =>
            setDraft((d) => ({ ...d, custom_field_key: e.target.value }))
          }
        >
          <option value="">{t("common.dash")}</option>
          {(schemas.data ?? [])
            .filter((s) => s.is_filterable)
            .map((s) => (
              <option key={s.id} value={s.key}>
                {s.label}
              </option>
            ))}
        </select>
        <Input
          aria-label={t("adminCustomFields.fields.label")}
          placeholder={t("adminCustomFields.fields.label")}
          value={draft.custom_field_value}
          onChange={(e) =>
            setDraft((d) => ({ ...d, custom_field_value: e.target.value }))
          }
        />
        <Button type="submit" size="sm" variant="outline">
          {t("common.filterApply")}
        </Button>
      </form>

      <label
        className="inline-flex items-center gap-2 text-sm"
        data-testid="employees-include-inactive"
      >
        <input
          type="checkbox"
          checked={includeInactive}
          onChange={(e) => setIncludeInactive(e.target.checked)}
          data-testid="employees-include-inactive-checkbox"
        />
        <span>{t("employees.filters.includeInactive")}</span>
      </label>

      <DataTable
        data={employees.data ?? []}
        columns={columns}
        testId="employees-table"
        isLoading={employees.isLoading}
        onRowClick={goToEmployee}
      />

      <EmployeeFormDialog
        open={creating}
        existing={null}
        onClose={() => setCreating(false)}
      />
    </div>
  );
}

function useColumns(
  schemas: CustomFieldSchemaResponse[],
  folders: Folder[],
  canDeactivate: boolean,
  onToggleActive: (employee: EmployeeResponse) => void,
  toggleBusy: boolean,
): ColumnDef<EmployeeResponse, unknown>[] {
  const { t } = useTranslation();
  return useMemo(() => {
    const folderNameById = new Map(folders.map((f) => [f.id, f.name]));
    const base: ColumnDef<EmployeeResponse, unknown>[] = [
      {
        id: "full_name",
        header: t("employees.columns.fullName"),
        cell: (info) => (
          <span data-testid={`employee-name-${info.row.original.id}`}>
            {info.row.original.full_name}
          </span>
        ),
      },
      {
        id: "position",
        header: t("employees.columns.position"),
        cell: (info) => info.row.original.position ?? t("common.dash"),
      },
      {
        id: "department",
        header: t("employees.columns.department"),
        cell: (info) => info.row.original.department ?? t("common.dash"),
      },
      {
        id: "aliases",
        header: t("employees.columns.aliases"),
        cell: (info) => info.row.original.aliases.join(", "),
      },
      {
        id: "meeting_count",
        header: t("employees.columns.meetingCount"),
        accessorFn: (row) => row.meeting_count,
        cell: (info) => (
          <span
            className="tabular-nums"
            data-testid={`employee-meeting-count-${info.row.original.id}`}
          >
            {info.row.original.meeting_count}
          </span>
        ),
        sortingFn: "basic",
      },
      {
        id: "analysis_count",
        header: t("employees.columns.analysisCount"),
        accessorFn: (row) => row.analysis_count,
        cell: (info) => (
          <span
            className="tabular-nums"
            data-testid={`employee-analysis-count-${info.row.original.id}`}
          >
            {info.row.original.analysis_count}
          </span>
        ),
        sortingFn: "basic",
      },
      {
        id: "folders",
        header: t("employees.columns.folders"),
        cell: (info) => (
          <FolderChips
            ids={info.row.original.folder_ids}
            nameById={folderNameById}
            dash={t("common.dash")}
            rowId={info.row.original.id}
          />
        ),
      },
      {
        id: "account",
        header: t("employees.columns.account"),
        cell: (info) => (
          <AccountCell
            employee={info.row.original}
            activeLabel={t("employees.account.active")}
            inactiveLabel={t("employees.account.inactive")}
            dash={t("common.dash")}
          />
        ),
      },
    ];
    for (const s of schemas) {
      base.push({
        id: `cf_${s.key}`,
        header: s.label,
        cell: (info) => {
          const v = info.row.original.custom_field_values[s.key];
          if (v === undefined || v === null || v === "") {
            return t("common.dash");
          }
          return String(v);
        },
      });
    }
    if (canDeactivate) {
      base.push({
        id: "actions",
        header: "",
        cell: (info) => {
          const row = info.row.original;
          return (
            <Button
              type="button"
              size="sm"
              variant={row.is_active ? "outline" : "default"}
              disabled={toggleBusy}
              data-testid={`employee-toggle-active-${row.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onToggleActive(row);
              }}
            >
              {row.is_active
                ? t("employees.actions.deactivate")
                : t("employees.actions.restore")}
            </Button>
          );
        },
      });
    }
    return base;
  }, [t, schemas, folders, canDeactivate, onToggleActive, toggleBusy]);
}

function FolderChips({
  ids,
  nameById,
  dash,
  rowId,
}: {
  ids: string[];
  nameById: Map<string, string>;
  dash: string;
  rowId: string;
}) {
  if (ids.length === 0) return <span>{dash}</span>;
  return (
    <div
      className="flex flex-wrap gap-1"
      data-testid={`employee-folders-${rowId}`}
    >
      {ids.map((id) => (
        <span
          key={id}
          className="inline-flex items-center rounded-full border bg-muted/30 px-2 py-0.5 text-xs"
        >
          {nameById.get(id) ?? id}
        </span>
      ))}
    </div>
  );
}

function AccountCell({
  employee,
  activeLabel,
  inactiveLabel,
  dash,
}: {
  employee: EmployeeResponse;
  activeLabel: string;
  inactiveLabel: string;
  dash: string;
}) {
  const linked = employee.linked_user;
  if (linked === null) {
    return <span data-testid={`employee-account-${employee.id}`}>{dash}</span>;
  }
  return (
    <span
      className="flex items-center gap-2"
      data-testid={`employee-account-${employee.id}`}
    >
      <span>{linked.email}</span>
      <span
        className={
          linked.is_active
            ? "inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300"
            : "inline-flex items-center rounded-full border border-muted-foreground/30 bg-muted/30 px-2 py-0.5 text-xs font-medium text-muted-foreground"
        }
        data-testid={`employee-account-badge-${employee.id}`}
      >
        {linked.is_active ? activeLabel : inactiveLabel}
      </span>
    </span>
  );
}
